/**
 * Action Center payment cards → the pages that already handle payments (2026-09-16 redesign).
 *
 * "Payment failed" → Send new link: the card never sends anything itself. It opens the patient's profile
 * with the existing Collect Payment dialog pre-filled (amount + what it pays for), so the new link goes
 * out through the one flow that already enforces the SMS template, credit/segment limits and text
 * consent — and the operator reviews it before sending (the confirm step; failed payments are never
 * retried automatically). createMoolahPaymentRecord publishes PAYMENT_LINK_RESENT, which clears the card
 * for everyone.
 *
 * "Payment received" → View payment: My Account → Patient Payments on the day it arrived (admins with
 * Open Dental write-back on; everyone else gets View patient).
 *
 * This file is the URL contract between the ends: the cards build the queries, PatientProfile and
 * PatientPaymentsTab parse them. Import-free: cypress/node-tests/action-center-payment-link.test.js
 * executes it.
 */

export const SEND_NEW_LINK_QUERY_KEY = 'collectPayment';

// Values match PAYMENT_PURPOSE in src/utils/collectPaymentPurpose.js (pinned by the node test).
const PURPOSE_BY_INTENT = { balance: 'balance', prepayment: 'deposit' };

function positiveInt(v) {
  const n = Number(v);
  return Number.isInteger(n) && n > 0 ? n : null;
}

/** Route for the card button, or null when the alert lacks what the dialog needs. */
export function buildSendNewLinkRoute(siteID, failedPayment) {
  const fp = failedPayment || {};
  const patNum = positiveInt(fp.patNum);
  if (!siteID || !patNum) return null;
  const query = { [SEND_NEW_LINK_QUERY_KEY]: '1', purpose: PURPOSE_BY_INTENT[fp.intent] || 'balance' };
  const amount = Number(fp.amount);
  if (Number.isFinite(amount) && amount > 0) query.amount = amount.toFixed(2);
  const treatPlanNum = positiveInt(fp.treatPlanNum);
  if (query.purpose === 'deposit' && treatPlanNum) query.treatPlanNum = String(treatPlanNum);
  return { name: 'patientprofile', params: { patientID: `${siteID}-${patNum}` }, query };
}

// ── "Payment received" → View payment ────────────────────────────────────────
// My Account → Patient Payments (/app/my-account?tab=payments) lists every Moolah payment and whether it
// reached Open Dental. The card opens it on the day the payment arrived, searched to that patient.

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const UNKNOWN_PATIENT = 'unknown patient';

/**
 * Who can see that page's list: Admin / Super Admin, on a site with Open Dental write-back on — the same
 * gate as MyAccount.vue `showOdSyncCard`. Everyone else sees a placeholder there, so their card keeps
 * "View patient" instead.
 */
export function canViewPatientPaymentsPage({ role, odWriteEnabled } = {}) {
  const admin = role === 'Admin' || role === 'Super Admin';
  return admin && String(odWriteEnabled == null ? '' : odWriteEnabled).trim().toUpperCase() === 'TRUE';
}

/** Route for "View payment", or null without a usable practice-day ('YYYY-MM-DD'). */
export function buildViewPaymentRoute({ dayKey, patientName } = {}) {
  const day = String(dayKey || '');
  if (!DAY_RE.test(day)) return null;
  const query = { tab: 'payments', from: day, to: day };
  const name = String(patientName || '').trim();
  if (name && name.toLowerCase() !== UNKNOWN_PATIENT) query.search = name;
  return { name: 'MyAccount', query };
}

/**
 * PatientPaymentsTab side: `{ from, to, search }` when the URL names a date range, else null (the tab then
 * opens on Today as always). A reversed or malformed range is ignored rather than guessed at.
 */
export function parseViewPaymentQuery(query) {
  const q = query || {};
  if (String(q.tab || '').toLowerCase() !== 'payments') return null;
  const from = String(q.from || '');
  const to = String(q.to || '');
  if (!DAY_RE.test(from) || !DAY_RE.test(to) || from > to) return null;
  const search = typeof q.search === 'string' ? q.search.trim().slice(0, 100) : '';
  return { from, to, search };
}

/**
 * PatientProfile side: `{ amount, purpose, treatPlanNum }` when the URL asks for the dialog, else null.
 * Hand-edited or junk values degrade to "open the dialog with its normal defaults", never to a guess.
 */
export function parseSendNewLinkQuery(query) {
  const q = query || {};
  if (q[SEND_NEW_LINK_QUERY_KEY] !== '1') return null;
  const purpose = q.purpose === 'deposit' ? 'deposit' : 'balance';
  const amountNum = Number(q.amount);
  const amount = Number.isFinite(amountNum) && amountNum > 0 ? Math.round(amountNum * 100) / 100 : null;
  const treatPlanNum = purpose === 'deposit' ? positiveInt(q.treatPlanNum) : null;
  return { amount, purpose, treatPlanNum };
}
